/* eslint-disable max-classes-per-file */
/* eslint-disable no-useless-constructor */
import { TimeSinceLastGroomEnum } from '@rover/rsdk/src/apiClient/latest/model/timeSinceLastGroomEnum';
import { CONCEPTS } from '@rover/rsdk/src/concepts';
import type { ConsentPurpose } from '@rover/rsdk/src/modules/Analytics';
import { AnalyticsEvent } from '@rover/types/src/Analytics';

abstract class GroomingEvent extends AnalyticsEvent {
  consentPurpose: ConsentPurpose = 'performance';

  concept = CONCEPTS.service_offerings.grooming;
}

type GroomingPages =
  | 'experience'
  | 'yearsExperience'
  | 'signUpHub'
  | 'groomingGate'
  | 'groomingSettings'
  | 'groomingProfile'
  | 'groomingAddTestimonials'
  | 'groomingAddPhotos'
  | 'groomingSafetyQuiz'
  | 'groomingProfileSubmitted';

type GroomingSteps = 'settings' | 'profile' | 'testimonials' | 'photos' | 'safetyQuiz';

/**
 * &nbsp;
 *
 * # Description
 *
 * When a user views one of the grooming sign up pages, fire an event.
 *
 * &nbsp;
 */
export class GroomingSignUpPageView extends GroomingEvent {
  eventName = 'grooming-sign-up-page-view';

  constructor(eventProperties: { page: GroomingPages }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When a user clicks save/done on any of the sign up page steps, fire an event.
 *
 * &nbsp;
 */
export class GroomingSignUpStepComplete extends GroomingEvent {
  eventName = 'grooming-sign-up-step-complete';

  constructor(eventProperties: { stepCompleted: GroomingSteps }) {
    const observabilityTags = eventProperties; // rename for clarity
    // passing in observabilityTags for Datadog logs, and eventProperties for the analytics event itself
    super(eventProperties, observabilityTags);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When a user clicks to start the safety & skills quiz, fire an event.
 *
 * &nbsp;
 */
export class GroomingSafetyQuizStart extends GroomingEvent {
  eventName = 'grooming-safety-quiz-start';
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When a user clicks to start the safety & skills quiz, fire an event.
 *
 * &nbsp;
 */
export class GroomingSafetyQuizCompleted extends GroomingEvent {
  eventName = 'grooming-safety-quiz-completed';
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When a user views a groomer contact page, fire an event.
 * This is primarily being added to support SEM tracking.
 *
 * &nbsp;
 */
export class GroomingContactPageView extends GroomingEvent {
  eventName = 'grooming-contact-page-view';

  consentPurpose: ConsentPurpose = 'marketing';
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When a user views a groomer profile page, fire an event.
 *  This is primarily being added to support SEM tracking.
 *
 * &nbsp;
 */
export class GroomingProfilePageView extends GroomingEvent {
  eventName = 'grooming-profile-page-view';

  consentPurpose: ConsentPurpose = 'marketing';
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When a user selects "I'm flexible on dates", fire an event.
 *
 * &nbsp;
 */
export class GroomingContactFlexDatesClick extends GroomingEvent {
  eventName = 'grooming-contact-flex-dates-click';
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When the "Send request" button becomes visible on the page, fire an event.
 * This tracks when a user reaches the end of the contact form.
 *
 * &nbsp;
 */
export class GroomingContactScroll extends GroomingEvent {
  eventName = 'grooming-contact-scroll';
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When a user selects an option for "When's the last time your pet was groomed?", fire an event.
 *
 * &nbsp;
 */
export class GroomingContactLastGroomSelect extends GroomingEvent {
  eventName = 'grooming-contact-last-groom-select';

  constructor(eventProperties: { buttonSelect: TimeSinceLastGroomEnum }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When a user clicks on the contact button on the groomer profile page, fire an event.
 *
 * &nbsp;
 */
export class GroomingProfileContactClick extends GroomingEvent {
  eventName = 'grooming-profile-contact-click';

  constructor(eventProperties: { button: 'primaryDesktop' | 'footer' }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When a user clicks to open the photo gallery, fire an event.
 *
 * &nbsp;
 */
export class GroomingProfilePhotosClick extends GroomingEvent {
  eventName = 'grooming-profile-photos-click';
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When a user clicks to read more content, fire an event.
 *
 * &nbsp;
 */
export class GroomingProfileReadMoreClick extends GroomingEvent {
  // Note: event spec uses 'grooming-profile-contact-click' for this event name
  eventName = 'grooming-profile-contact-click';

  constructor(eventProperties: {
    location: 'about' | 'serviceDescription' | 'availability' | 'review' | 'allReviews';
  }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When a user clicks to view all services reviews, fire an event.
 *
 * &nbsp;
 */
export class GroomingProfileAllServicesReviewsClick extends GroomingEvent {
  eventName = 'grooming-profile-all-services-reviews-click';
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When a user views the grooming search page, fire an event. This should only
 * fire on page load with data, and does not need to fire each time someone
 * filters the search results. Don't fire in the event we have an error loading
 * page data. This is primarily being added to support SEM tracking.
 *
 * &nbsp;
 */
export class GroomingSearchPageView extends GroomingEvent {
  eventName = 'grooming-search-page-view';

  consentPurpose: ConsentPurpose = 'marketing';
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When a user clicks on a groomer search card, fire an event.
 *
 * &nbsp;
 */
export type GroomingSearchCardSection = 'Recommended-professionals' | 'Other-professionals';

export class GroomingSearchCardClick extends GroomingEvent {
  eventName = 'grooming-search-card-click';

  constructor(eventProperties: { section: GroomingSearchCardSection }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When a user clicks the "Add rates" button on the grooming settings page, fire an event.
 *
 * &nbsp;
 */
export class GroomingSettingsRatesClick extends GroomingEvent {
  eventName = 'grooming-settings-rates-click';

  constructor(eventProperties: { button: 'add-rates' | 'manage-rates' }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When a user clicks the grooming search button on the mweb homepage service selector.
 *
 * &nbsp;
 */
export class GroomingHomepageSearchClick extends GroomingEvent {
  eventName = 'grooming_homepage_search_click';
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When a user clicks the grooming service entry on the desktop rebooking homepage.
 *
 * &nbsp;
 */
export class GroomingHomepageRebookServiceClick extends GroomingEvent {
  eventName = 'grooming_homepage_rebook_service_grooming_click';
}
